import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Star } from "lucide-react";

const CITIES = ["Campinas", "Hortolândia", "Sumaré", "Valinhos", "Vinhedo", "Paulínia", "Americana", "Indaiatuba"];
const TIPOS = ["CLT", "PJ", "Temporário", "Estágio", "Freelancer", "Meio período"];

export default function PostJob() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    cargo: "", cidade: user.cidade || "", tipo: "CLT", salario: "",
    descricao: "", whatsapp: user.whatsapp || "", email: "", url: "", featured: false,
    terms_accepted: false,
  });

  useEffect(() => {
    api.get("/company/summary").then((r) => setSummary(r.data));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.whatsapp.trim() && !form.email.trim() && !form.url.trim()) {
      toast.error("Informe ao menos um canal de candidatura (WhatsApp, e-mail ou link).");
      return;
    }
    if (!form.terms_accepted) {
      toast.error("Aceite os Termos de Uso e as regras de segurança para publicar.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/company/jobs", form);
      toast.success("Vaga enviada! Ela aparecerá no site após aprovação do administrador.");
      navigate("/empresa");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao publicar vaga.");
    } finally {
      setSaving(false);
    }
  };

  const canFeature = (summary?.featured_credits > 0) || (summary?.plan_active && summary?.plan_allow_featured);

  return (
    <div className="max-w-3xl mx-auto px-5 py-10">
      <Link to="/empresa" className="inline-flex items-center gap-2 text-slate-600 font-bold hover:text-slate-950 mb-6">
        <ArrowLeft size={18} /> Voltar ao painel
      </Link>
      <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-950 mb-2">Publicar nova vaga</h1>
      <p className="text-slate-600 mb-8">
        Créditos avulsos: <strong>{summary?.job_credits ?? 0}</strong> • Créditos destaque: <strong>{summary?.featured_credits ?? 0}</strong>
        {summary?.plan_active && <> • Plano ativo ({summary.plan_max_jobs} vagas)</>}
      </p>

      <form onSubmit={submit} className="bg-white rounded-3xl p-8 border border-slate-200 grid gap-4">
        <input data-testid="job-cargo" required placeholder="Cargo (ex: Auxiliar de Logística)" value={form.cargo}
          onChange={(e) => setForm({ ...form, cargo: e.target.value })}
          className="w-full rounded-xl px-4 py-3 border border-slate-200 outline-none focus:border-blue-500" />
        <div className="grid sm:grid-cols-2 gap-4">
          <select data-testid="job-cidade" required value={form.cidade}
            onChange={(e) => setForm({ ...form, cidade: e.target.value })}
            className="w-full rounded-xl px-4 py-3 border border-slate-200 outline-none focus:border-blue-500">
            <option value="">Cidade</option>
            {CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select data-testid="job-tipo" value={form.tipo}
            onChange={(e) => setForm({ ...form, tipo: e.target.value })}
            className="w-full rounded-xl px-4 py-3 border border-slate-200 outline-none focus:border-blue-500">
            {TIPOS.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <input data-testid="job-salario" required placeholder="Salário (ex: R$ 2.400 ou A combinar)" value={form.salario}
          onChange={(e) => setForm({ ...form, salario: e.target.value })}
          className="w-full rounded-xl px-4 py-3 border border-slate-200 outline-none focus:border-blue-500" />
        <textarea data-testid="job-descricao" required placeholder="Descrição da vaga, requisitos e benefícios" rows={5} value={form.descricao}
          onChange={(e) => setForm({ ...form, descricao: e.target.value })}
          className="w-full rounded-xl px-4 py-3 border border-slate-200 outline-none focus:border-blue-500" />

        <div className="rounded-2xl border border-slate-200 p-4">
          <p className="font-extrabold text-slate-800 mb-1">Canais de candidatura</p>
          <p className="text-sm text-slate-500 mb-3">Preencha ao menos um. O candidato verá os canais escolhidos.</p>
          <div className="grid gap-3">
            <input data-testid="job-whatsapp" placeholder="WhatsApp com DDD (ex: 19 99999-9999)" value={form.whatsapp}
              onChange={(e) => setForm({ ...form, whatsapp: e.target.value })}
              className="w-full rounded-xl px-4 py-3 border border-slate-200 outline-none focus:border-blue-500" />
            <input data-testid="job-email" type="email" placeholder="E-mail para currículos (opcional)" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full rounded-xl px-4 py-3 border border-slate-200 outline-none focus:border-blue-500" />
            <input data-testid="job-url" placeholder="Link de candidatura (opcional)" value={form.url}
              onChange={(e) => setForm({ ...form, url: e.target.value })}
              className="w-full rounded-xl px-4 py-3 border border-slate-200 outline-none focus:border-blue-500" />
          </div>
        </div>

        <label className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer ${form.featured ? "border-emerald-400 bg-emerald-50" : "border-slate-200"} ${!canFeature ? "opacity-50 cursor-not-allowed" : ""}`}>
          <input data-testid="job-featured" type="checkbox" disabled={!canFeature} checked={form.featured}
            onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="w-5 h-5" />
          <span className="font-bold text-slate-800 flex items-center gap-2">
            <Star size={16} className="text-emerald-500" /> Publicar como vaga em destaque
          </span>
        </label>
        {!canFeature && (
          <p className="text-sm text-slate-500 -mt-2">
            Para destacar, compre "Vaga em destaque" ou o Plano Pro em <Link to="/planos" className="text-emerald-700 font-bold">/planos</Link>.
          </p>
        )}

        <label className="flex items-start gap-3 p-4 rounded-xl border border-slate-200 bg-slate-50 cursor-pointer">
          <input
            data-testid="job-terms"
            type="checkbox"
            required
            checked={form.terms_accepted}
            onChange={(e) => setForm({ ...form, terms_accepted: e.target.checked })}
            className="w-5 h-5 mt-0.5 shrink-0"
          />
          <span className="text-sm text-slate-700 leading-relaxed">
            Declaro que esta vaga é real, não cobra do candidato e aceito os <Link to="/termos" target="_blank" className="text-blue-700 font-bold hover:underline">Termos de Uso</Link> e as regras de <Link to="/seguranca" target="_blank" className="text-blue-700 font-bold hover:underline">Segurança</Link>.
          </span>
        </label>

        <button data-testid="job-submit" disabled={saving || !form.terms_accepted}
          className="bg-emerald-400 text-slate-950 font-extrabold py-3.5 rounded-xl hover:bg-emerald-500 transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
          {saving ? <Loader2 className="animate-spin" size={18} /> : "Publicar vaga"}
        </button>
      </form>
    </div>
  );
}
